import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { EmailTagInput } from '@/components/ui/email-tag-input';
import { Send, SkipForward, X, ChevronRight, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { UnifiedInvoiceAPI } from '@/services/invoicing';
import { UnifiedInvoice, EmailFormData } from '@/types/invoicing';

interface BulkEmailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedInvoices: Set<number>;
  invoices: UnifiedInvoice[];
  clientMap: Record<number, string>;
  onEmailsSent: () => void;
}

// Get default email message
const getDefaultEmailMessage = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const previousMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1);
  const previousMonthName = previousMonth.toLocaleDateString('en-US', { month: 'long' });

  return `Dear Madame / Sir,\n\n` +
         `Please find attached our invoice for services provided through ${previousMonthName} ${currentYear}.\n\n` +
         `We are at your disposal if you have any questions.\n\n` +
         `With best regards,`;
};

type SendResult = { invoiceId: number; invoiceNumber: string; status: 'sent' | 'skipped' | 'error'; error?: string };

export function BulkEmailDialog({ 
  isOpen, 
  onClose, 
  selectedInvoices, 
  invoices, 
  clientMap, 
  onEmailsSent 
}: BulkEmailDialogProps) {
  const [emailFormData, setEmailFormData] = useState<EmailFormData>({
    message: '',
    additional_recipients: [],
    cc: [],
    bcc: [],
  });
  const [isSending, setIsSending] = useState(false);
  const [isLoadingRecipients, setIsLoadingRecipients] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [results, setResults] = useState<SendResult[]>([]);
  const [showSummary, setShowSummary] = useState(false);

  const selectedInvoicesList = invoices.filter(invoice => selectedInvoices.has(invoice.id));
  const currentInvoice = selectedInvoicesList[currentIndex] ?? null;
  const totalCount = selectedInvoicesList.length;

  // Load recipients for the current invoice
  const loadRecipientsForInvoice = useCallback(async (invoice: UnifiedInvoice) => {
    setIsLoadingRecipients(true);
    try {
      const recipientsData = await UnifiedInvoiceAPI.getRecipients(invoice.id);
      const toRecipients = recipientsData.recipients
        .filter(r => r.type === 'TO')
        .map(r => r.email);
      const ccRecipients = recipientsData.recipients
        .filter(r => r.type === 'CC')
        .map(r => r.email);
      const bccRecipients = recipientsData.recipients
        .filter(r => r.type === 'BCC')
        .map(r => r.email);

      setEmailFormData({
        message: getDefaultEmailMessage(),
        additional_recipients: toRecipients,
        cc: ccRecipients,
        bcc: bccRecipients,
      });
    } catch (error) {
      console.error(`Failed to fetch recipients for invoice ${invoice.invoice_number}:`, error);
      setEmailFormData({
        message: getDefaultEmailMessage(),
        additional_recipients: [],
        cc: [],
        bcc: [],
      });
    } finally {
      setIsLoadingRecipients(false);
    }
  }, []);

  // When dialog opens or currentIndex changes, load recipients for the current invoice
  useEffect(() => {
    if (isOpen && currentInvoice && !showSummary) {
      loadRecipientsForInvoice(currentInvoice);
    }
  }, [isOpen, currentIndex, currentInvoice, showSummary, loadRecipientsForInvoice]);

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
      setResults([]);
      setShowSummary(false);
    }
  }, [isOpen]);

  const handleSendCurrent = async () => {
    if (!currentInvoice) return;
    setIsSending(true);
    try {
      await UnifiedInvoiceAPI.sendEmail(currentInvoice.id, emailFormData);
      setResults(prev => [...prev, { 
        invoiceId: currentInvoice.id, 
        invoiceNumber: currentInvoice.invoice_number, 
        status: 'sent' 
      }]);
    } catch (error: any) {
      const errorMsg = error?.response?.data?.error || error?.response?.data?.detail || 'Failed to send';
      setResults(prev => [...prev, { 
        invoiceId: currentInvoice.id, 
        invoiceNumber: currentInvoice.invoice_number,
        status: 'error',
        error: typeof errorMsg === 'string' ? errorMsg : 'Failed to send',
      }]);
    } finally {
      setIsSending(false);
      advanceToNext();
    }
  };

  const handleSkipCurrent = () => {
    if (!currentInvoice) return;
    setResults(prev => [...prev, { 
      invoiceId: currentInvoice.id, 
      invoiceNumber: currentInvoice.invoice_number, 
      status: 'skipped' 
    }]);
    advanceToNext();
  };

  const advanceToNext = () => {
    if (currentIndex + 1 < totalCount) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setShowSummary(true);
    }
  };

  const handleClose = () => {
    // If any were sent, notify parent to refresh
    const sentCount = results.filter(r => r.status === 'sent').length;
    if (sentCount > 0) {
      onEmailsSent();
    }
    setEmailFormData({ message: '', additional_recipients: [], cc: [], bcc: [] });
    setCurrentIndex(0);
    setResults([]);
    setShowSummary(false);
    onClose();
  };

  const handleFinish = () => {
    const sentCount = results.filter(r => r.status === 'sent').length;
    const errorCount = results.filter(r => r.status === 'error').length;
    const skippedCount = results.filter(r => r.status === 'skipped').length;

    if (sentCount > 0) {
      toast({
        title: 'Bulk Send Complete',
        description: `${sentCount} sent${skippedCount > 0 ? `, ${skippedCount} skipped` : ''}${errorCount > 0 ? `, ${errorCount} failed` : ''}`,
      });
    } else if (errorCount > 0) {
      toast({
        title: 'Sending Failed',
        description: `All ${errorCount} email(s) failed to send`,
        variant: 'destructive',
      });
    }

    handleClose();
  };

  // Summary view
  if (showSummary) {
    const sentCount = results.filter(r => r.status === 'sent').length;
    const errorCount = results.filter(r => r.status === 'error').length;
    const skippedCount = results.filter(r => r.status === 'skipped').length;

    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Complete</DialogTitle>
            <DialogDescription>
              Finished processing {totalCount} invoice{totalCount > 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Stats */}
            <div className="flex gap-4">
              {sentCount > 0 && (
                <Badge variant="default" className="text-sm px-3 py-1">
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                  {sentCount} Sent
                </Badge>
              )}
              {skippedCount > 0 && (
                <Badge variant="secondary" className="text-sm px-3 py-1">
                  <SkipForward className="h-3.5 w-3.5 mr-1" />
                  {skippedCount} Skipped
                </Badge>
              )}
              {errorCount > 0 && (
                <Badge variant="destructive" className="text-sm px-3 py-1">
                  <XCircle className="h-3.5 w-3.5 mr-1" />
                  {errorCount} Failed
                </Badge>
              )}
            </div>

            {/* Details */}
            <div className="max-h-60 overflow-y-auto border rounded-lg divide-y">
              {results.map((r, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span className="font-mono">{r.invoiceNumber}</span>
                  {r.status === 'sent' && (
                    <span className="flex items-center gap-1 text-green-600">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Sent
                    </span>
                  )}
                  {r.status === 'skipped' && (
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <SkipForward className="h-3.5 w-3.5" /> Skipped
                    </span>
                  )}
                  {r.status === 'error' && (
                    <span className="flex items-center gap-1 text-destructive" title={r.error}>
                      <XCircle className="h-3.5 w-3.5" /> Failed
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button onClick={handleFinish}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Individual invoice send view
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between pr-6">
            <span>Send Invoice Email</span>
            <Badge variant="outline" className="text-xs font-normal">
              {currentIndex + 1} of {totalCount}
            </Badge>
          </DialogTitle>
          <DialogDescription className="space-y-2">
            <div className="w-full bg-muted rounded-full h-1.5 mt-2">
              <div 
                className="bg-primary h-1.5 rounded-full transition-all duration-300" 
                style={{ width: `${((currentIndex) / totalCount) * 100}%` }} 
              />
            </div>
          </DialogDescription>
        </DialogHeader>

        {currentInvoice && (
          <div className="space-y-4">
            {/* Current invoice info */}
            <div className="flex items-center justify-between bg-muted/50 rounded-lg p-3">
              <div>
                <p className="font-mono font-medium text-sm">{currentInvoice.invoice_number}</p>
                <p className="text-xs text-muted-foreground">
                  {currentInvoice.client_name || clientMap[currentInvoice.client]}
                </p>
              </div>
              <div className="text-right">
                <p className="font-medium text-sm">
                  {new Intl.NumberFormat('en-BE', { style: 'currency', currency: 'EUR' }).format(Number(currentInvoice.total || 0))}
                </p>
                <Badge variant="secondary" className="text-[10px]">
                  {currentInvoice.invoice_type}
                </Badge>
              </div>
            </div>

            {/* Already processed indicator */}
            {results.length > 0 && (
              <div className="flex gap-2 text-xs text-muted-foreground">
                {results.filter(r => r.status === 'sent').length > 0 && (
                  <span className="flex items-center gap-0.5">
                    <CheckCircle2 className="h-3 w-3 text-green-600" />
                    {results.filter(r => r.status === 'sent').length} sent
                  </span>
                )}
                {results.filter(r => r.status === 'skipped').length > 0 && (
                  <span className="flex items-center gap-0.5">
                    <SkipForward className="h-3 w-3" />
                    {results.filter(r => r.status === 'skipped').length} skipped
                  </span>
                )}
                {results.filter(r => r.status === 'error').length > 0 && (
                  <span className="flex items-center gap-0.5 text-destructive">
                    <XCircle className="h-3 w-3" />
                    {results.filter(r => r.status === 'error').length} failed
                  </span>
                )}
              </div>
            )}

            {/* Email form */}
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Recipients (TO)</Label>
                <EmailTagInput
                  value={emailFormData.additional_recipients}
                  onChange={(emails) => setEmailFormData(prev => ({ 
                    ...prev, 
                    additional_recipients: emails
                  }))}
                  placeholder={isLoadingRecipients ? "Loading recipients..." : "Enter email address and press Enter..."}
                />
              </div>

              <div>
                <Label className="text-xs">CC</Label>
                <EmailTagInput
                  value={emailFormData.cc}
                  onChange={(emails) => setEmailFormData(prev => ({ 
                    ...prev, 
                    cc: emails
                  }))}
                  placeholder="CC email addresses..."
                />
              </div>

              <div>
                <Label className="text-xs">BCC</Label>
                <EmailTagInput
                  value={emailFormData.bcc}
                  onChange={(emails) => setEmailFormData(prev => ({ 
                    ...prev, 
                    bcc: emails
                  }))}
                  placeholder="BCC email addresses..."
                />
              </div>

              <div>
                <Label className="text-xs">Message</Label>
                <Textarea
                  value={emailFormData.message}
                  onChange={(e) => setEmailFormData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Enter your message..."
                  rows={5}
                />
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="ghost" size="sm" onClick={handleClose} disabled={isSending}>
            <X className="h-4 w-4 mr-1" />
            Cancel All
          </Button>
          <div className="flex gap-2 ml-auto">
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleSkipCurrent} 
              disabled={isSending || isLoadingRecipients}
            >
              <SkipForward className="h-4 w-4 mr-1" />
              Skip
            </Button>
            <Button 
              size="sm"
              onClick={handleSendCurrent}
              disabled={isSending || isLoadingRecipients}
            >
              {isSending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-foreground border-t-transparent mr-2" />
                  Sending...
                </>
              ) : isLoadingRecipients ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-foreground border-t-transparent mr-2" />
                  Loading...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-1" />
                  {currentIndex + 1 < totalCount ? 'Send & Next' : 'Send & Finish'}
                  {currentIndex + 1 < totalCount && <ChevronRight className="h-3 w-3 ml-0.5" />}
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
